/* 0.48.71 - Cascade: several parts filled together as one value, an
 * address being the usual case.
 *
 * Closed, it is one read-only line holding the parts already filled,
 * joined by the separator, with a chevron that opens the parts under it.
 * Open, the parts stand one under another at the Henaket field width:
 *
 *   - a dropdown takes its options from a library: one column is shown
 *     to the visitor, another is stored. It can be filtered by a
 *     dropdown above it (its options are the rows whose column holds the
 *     value chosen there), and is empty and disabled until that one is
 *     answered. Changing a choice clears the dropdowns below that depend
 *     on it.
 *   - a text field is typed, or filled from a column of the row chosen
 *     in a dropdown above it (the visitor may still change it, unless
 *     the admin says not).
 *
 * The element's value is an object keyed by each part's "Key in the
 * value", set only when every required part is filled; until then it is
 * '' so the field counts as empty for "required". An empty optional
 * part is left out or sent as null (empty_optional). keep_text adds the
 * summary line as `text`.
 *
 * The parts being filled are kept here, not in the page's values: only
 * the finished value is published. */
import { useEffect, useMemo, useRef, useState } from 'react';
import HenaketDropdown from './HenaketDropdown';
import fieldStyles from './Input.module.css';
import styles from './CascadeField.module.css';

export const CASCADE_SEPARATORS = {
  comma: ', ', slash: ' / ', dot: ' · ', bar: ' | ', space: ' ',
};

// Library rows, by library + filter + language, shared by every cascade
// on the page (two address fields on one form fetch a region list once).
const rowCache = new Map();
function fetchRows(library, filterColumn, filterValue, lang) {
  const key = [library, filterColumn || '', filterValue || '', lang || ''].join('|');
  if (rowCache.has(key)) return rowCache.get(key);
  const params = new URLSearchParams();
  params.set('limit', '5000');
  if (lang) params.set('lang', lang);
  if (filterColumn) {
    params.set('filter_column', filterColumn);
    params.set('filter_values', String(filterValue));
  }
  const p = fetch(`/api/form-libraries/${encodeURIComponent(library)}/rows?${params}`)
    .then(r => (r.ok ? r.json() : { rows: [] }))
    .then(d => (Array.isArray(d.rows) ? d.rows : []))
    .catch(() => { rowCache.delete(key); return []; });
  rowCache.set(key, p);
  return p;
}

/* 0.48.78 - "Each word capitalized": the first letter of every word
   (after a space, hyphen or bracket) a capital, the rest small. ԼՈՌԻ
   becomes Լոռի, ՄԱԼԱԹԻԱ-ՍԵԲԱՍՏԻԱ Մալաթիա-Սեբաստիա. */
export function capitalizeWords(text) {
  return String(text || '').toLocaleLowerCase()
    // Armenian capitals write the letter և as ԵՎ: inside a word it is
    // և again (ԵՐԵՎԱՆ -> Երևան), at its start it stays Եվ (Եվրոպա).
    .replace(/(\p{L})եվ/gu, '$1և')
    .replace(/(^|[\s\-(«"'/])(\S)/gu, (m, pre, ch) => pre + ch.toLocaleUpperCase());
}
/* 0.48.79 - All capitals. The Armenian և becomes ԵՎ (as capitals are
   written), not ԵՒ, which toUpperCase gives. */
export function upperAll(text) {
  return String(text || '').replace(/և/g, 'ԵՎ').toLocaleUpperCase();
}
const str = (v) => (v === null || v === undefined ? '' : String(v));
const partsOf = (el) => (Array.isArray(el && el.parts) ? el.parts.filter(p => p && p.id) : []);
const isDropdown = (p) => p.kind === 'dropdown';
const partRequired = (p) => p.required !== false;
const storedOf = (p, row) => str(row && (p.value_column ? row[p.value_column] : (row.business_id ?? row.id)));
const shownOf = (p, row) => str(row && (p.label_column ? row[p.label_column] : storedOf(p, row)));

/* The value an element holds (object or ''), back to one string per part. */
function draftFromValue(el, value) {
  const out = {};
  const obj = value && typeof value === 'object' ? value : {};
  for (const p of partsOf(el)) out[p.id] = str(obj[p.key || p.id]);
  return out;
}

export default function CascadeField({
  el, value, onChange, lang, resolveText, disabled = false, required = false,
  invalid = false, id, emptyText = '', selectText = '',
}) {
  const parts = useMemo(() => partsOf(el), [el]);
  const tx = (s) => (typeof resolveText === 'function' ? resolveText(s || '') : (s || ''));
  const sep = CASCADE_SEPARATORS[el.separator] ?? CASCADE_SEPARATORS.comma;
  const [draft, setDraft] = useState(() => draftFromValue(el, value));
  const [rows, setRows] = useState({}); // part id -> rows for its current filter
  // 0.48.75 - The filter value each part's rows were loaded for.
  const rowsFor = useRef({});
  const filled = (d) => parts.some(p => d[p.id] !== '' && d[p.id] !== undefined);
  const completeOf = (d) => parts.length > 0 && parts.every(p => !partRequired(p) || str(d[p.id]).trim() !== '');
  const [open, setOpen] = useState(() => {
    const start = el.start || 'auto';
    if (start === 'open') return true;
    if (start === 'closed') return false;
    return !completeOf(draftFromValue(el, value));
  });
  const boxRef = useRef(null);

  // A value set from outside (a rule, a flow coming back) replaces the
  // parts being filled when it differs from what they make.
  const lastEmitted = useRef(JSON.stringify(value ?? ''));
  // 0.48.87 - Parts set from outside, not yet made into a whole value.
  const fromOutside = useRef(false);
  useEffect(() => {
    const s = JSON.stringify(value ?? '');
    if (s === lastEmitted.current) return;
    lastEmitted.current = s;
    if (value && typeof value === 'object') { fromOutside.current = true; setDraft(draftFromValue(el, value)); }
    else if (value === '' || value === null || value === undefined) {
      // Cleared from outside: the parts empty too. A partly filled
      // cascade publishes '' itself, so only a real clear (nothing of it
      // left) is followed.
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  /* 0.48.73 - The value a dropdown's filter compares with: a column of
     the row chosen in the dropdown above (filter_parent_column), or,
     without one, what that dropdown stores. */
  const parentValueOf = (p, d = draft, rowsNow = rows) => {
    if (!p.filter_part) return '';
    const parent = parts.find(x => x.id === p.filter_part);
    if (!parent) return '';
    const stored = str(d[parent.id]);
    if (!stored) return '';
    if (!p.filter_parent_column) return stored;
    const row = (rowsNow[parent.id] || []).find(r => storedOf(parent, r) === stored);
    return row ? str(row[p.filter_parent_column]) : '';
  };
  // Options of each dropdown, for its parent's current choice.
  const filterKey = parts.map(p => (isDropdown(p) ? `${p.id}:${parentValueOf(p)}` : '')).join('|');
  useEffect(() => {
    let gone = false;
    for (const p of parts) {
      if (!isDropdown(p) || !p.library) continue;
      const parentVal = parentValueOf(p);
      if (p.filter_part && (!parentVal || !p.filter_column)) {
        rowsFor.current[p.id] = '';
        setRows(prev => (prev[p.id] && prev[p.id].length === 0 ? prev : { ...prev, [p.id]: [] }));
        continue;
      }
      fetchRows(p.library, p.filter_part ? p.filter_column : '', parentVal, lang).then(list => {
        if (!gone) { rowsFor.current[p.id] = parentVal; setRows(prev => ({ ...prev, [p.id]: list })); }
      });
    }
    return () => { gone = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey, lang, parts]);

  /* 0.48.87 - A dropdown's options are known for the choice above only
     once that choice's own row is loaded: before then the filter value
     reads as empty, and an empty list must not clear a valid choice. */
  const optionsKnown = (p) => {
    if (!Array.isArray(rows[p.id])) return false;
    if (rowsFor.current[p.id] !== parentValueOf(p)) return false;
    return !(p.filter_part && str(draft[p.filter_part]) && !parentValueOf(p));
  };

  const rowFor = (p, d = draft) => (rows[p.id] || []).find(r => storedOf(p, r) === str(d[p.id])) || null;

  const summaryOf = (d, rowsNow = rows) => parts
    .filter(p => p.in_summary !== false)
    .map(p => {
      let text = '';
      if (isDropdown(p)) {
        const row = (rowsNow[p.id] || []).find(r => storedOf(p, r) === str(d[p.id]));
        text = row ? shownOf(p, row) : '';
      } else {
        text = str(d[p.id]).trim();
      }
      if (!text) return '';
      // Library names only (a dropdown, or a field filled from one):
      // what the visitor typed keeps its letters.
      if (el.summary_case === 'words' && (isDropdown(p) || p.fill_part)) text = capitalizeWords(text);
      const pre = tx(p.prefix).trim();
      const whole = pre ? `${pre} ${text}` : text;
      // 0.48.79 - All letters capital: the whole line, typed text too.
      return el.summary_case === 'upper' ? upperAll(whole) : whole;
    })
    .filter(Boolean)
    .join(sep);

  const valueOf = (d, rowsNow) => {
    if (!completeOf(d)) return '';
    const out = {};
    for (const p of parts) {
      const k = (p.key || p.id).trim();
      if (!k) continue;
      const v = str(d[p.id]).trim();
      if (v !== '') out[k] = v;
      else if (el.empty_optional === 'null') out[k] = null;
    }
    if (el.keep_text !== false) out.text = summaryOf(d, rowsNow);
    return out;
  };

  const publish = (d, rowsNow = rows) => {
    const v = valueOf(d, rowsNow);
    const s = JSON.stringify(v);
    if (s === lastEmitted.current) return;
    lastEmitted.current = s;
    onChange(v);
  };

  /* A change in one part: dropdowns that depend on it (directly or
     through another) are cleared, text fields filled from any of those
     are filled again from the new row (or emptied). */
  const change = (pid, val) => {
    const next = { ...draft, [pid]: val };
    const changed = new Set([pid]);
    for (const p of parts) {
      if (isDropdown(p) && p.filter_part && changed.has(p.filter_part)) {
        next[p.id] = '';
        changed.add(p.id);
      }
    }
    for (const p of parts) {
      if (isDropdown(p) || !p.fill_part || !changed.has(p.fill_part)) continue;
      const src = parts.find(x => x.id === p.fill_part);
      const row = src ? (rows[src.id] || []).find(r => storedOf(src, r) === str(next[src.id])) : null;
      next[p.id] = row && p.fill_column ? str(row[p.fill_column]) : '';
    }
    setDraft(next);
    publish(next);
  };

  // Rows arriving for an already chosen value (a value from outside or
  // a flow coming back) refresh the summary inside the value.
  /* 0.48.75 - And a choice that is not among its dropdown's options for
     the current choice above (a value from outside, an older value) is
     cleared with everything below it, as a change would. */
  useEffect(() => {
    for (const p of parts) {
      if (!isDropdown(p) || !p.filter_part) continue;
      const v = str(draft[p.id]);
      if (!v || !optionsKnown(p)) continue;
      if (!rows[p.id].some(r => storedOf(p, r) === v)) { change(p.id, ''); return; }
    }
    if (completeOf(draft)) publish(draft, rows);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  /* 0.48.87 - Parts a rule set one by one (set address.region to ...):
     once every chosen dropdown has its options, a choice that is not
     among them is cleared as a change would, and the value is made
     again from the parts, with its summary line, or '' while a
     required part is empty. */
  useEffect(() => {
    if (!fromOutside.current) return;
    const ready = parts.every(p => !isDropdown(p) || !p.library || !str(draft[p.id]) || optionsKnown(p));
    if (!ready) return;
    fromOutside.current = false;
    // Opening "auto": open while a required part is empty, as on first show.
    if ((el.start || 'auto') === 'auto') setOpen(!completeOf(draft));
    for (const p of parts) {
      if (!isDropdown(p) || !p.library) continue;
      const v = str(draft[p.id]);
      if (v && !rows[p.id].some(r => storedOf(p, r) === v)) { change(p.id, ''); return; }
    }
    publish(draft, rows);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, rows]);

  // Closes when every required part is filled and the visitor leaves it.
  const onBlurBox = (ev) => {
    if (el.close_when_complete === false) return;
    const to = ev.relatedTarget;
    if (to && boxRef.current && boxRef.current.contains(to)) return;
    // A dropdown's list is drawn outside the box: wait a tick.
    setTimeout(() => {
      const a = document.activeElement;
      if (a && boxRef.current && boxRef.current.contains(a)) return;
      if (completeOf(draft)) setOpen(false);
    }, 150);
  };

  // An error on the field ("show errors on empty required fields")
  // opens the parts, so the empty ones can be seen.
  useEffect(() => { if (invalid) setOpen(true); }, [invalid]);

  const summary = summaryOf(draft) || (filled(draft) ? '' : '');
  const panelId = `${id || 'cascade'}-parts`;
  /* 0.48.81 - Opened by the visitor (click, Enter, arrow down): the
     focus goes to the first part, the region, rather than staying on
     the line. */
  const focusFirstPart = () => window.setTimeout(() => {
    const n = boxRef.current && boxRef.current.querySelector(`#${CSS.escape(`${id || 'cascade'}-p0`)}`);
    if (n && typeof n.focus === 'function') n.focus({ preventScroll: true });
  }, 0);
  const toggle = () => {
    if (disabled && !open) return;
    if (!open) focusFirstPart();
    setOpen(o => !o);
  };

  return (
    <div className={styles.box} ref={boxRef} onBlur={onBlurBox}>
      <div className={`${styles.head}${open ? ` ${styles.headOpen}` : ''}`}
           {...(open ? { 'data-autofocus-skip': '' } : {})}>
        {/* 0.48.78 - A button rather than a read-only input, so a long
            summary wraps onto more lines instead of being cut. */}
        <button id={id} type="button"
                className={`${fieldStyles.input} ${styles.summary}${summary ? '' : ` ${styles.summaryEmpty}`}${invalid ? ` ${fieldStyles.inputError}` : ''}`}
                aria-expanded={open} aria-controls={panelId}
                aria-invalid={invalid || undefined}
                onClick={toggle}
                onKeyDown={(ev) => { if (ev.key === 'ArrowDown' && !open) { ev.preventDefault(); toggle(); } }}>
          {summary || tx(emptyText) || '\u00a0'}
        </button>
        <button type="button" className={styles.chevron} onClick={toggle}
                aria-expanded={open} aria-controls={panelId}
                aria-label={open ? 'Close' : 'Open'}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"
               className={open ? styles.chevronUp : undefined}>
            <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>
      <div id={panelId} className={styles.panel} hidden={!open}>
        {parts.map((p, i) => {
          const pid = `${id || 'cascade'}-p${i}`;
          const req = partRequired(p);
          const label = tx(p.label);
          const hint = tx(p.hint);
          const missing = invalid && req && str(draft[p.id]).trim() === '';
          let control;
          if (isDropdown(p)) {
            const parentEmpty = !!p.filter_part && !parentValueOf(p);
            const opts = (rows[p.id] || []).map(r => ({ value: storedOf(p, r), label: shownOf(p, r) }));
            control = (
              <HenaketDropdown id={pid} value={str(draft[p.id])}
                               onChange={(v) => change(p.id, str(v))}
                               options={opts}
                               placeholder={tx(p.placeholder) || tx(selectText) || '- select -'}
                               disabled={disabled || parentEmpty}
                               required={req} error={missing}
                               disableAutoExpand />
            );
          } else {
            const fromDropdown = !!p.fill_part;
            const lockedHere = disabled || (fromDropdown && p.fill_editable === false);
            control = (
              <input id={pid} type="text" value={str(draft[p.id])}
                     onChange={(ev) => change(p.id, ev.target.value)}
                     disabled={lockedHere} required={req} autoComplete="off"
                     placeholder={tx(p.placeholder)}
                     className={`${fieldStyles.input}${missing ? ` ${fieldStyles.inputError}` : ''}`} />
            );
          }
          return (
            <div key={p.id} className={styles.part}>
              {label && (
                <label htmlFor={pid} className={fieldStyles.label}>
                  {req && <span className={fieldStyles.required} aria-hidden="true">*</span>}
                  {label}
                </label>
              )}
              {hint && <span className={`${fieldStyles.help} ${fieldStyles.helpAbove}`}>{hint}</span>}
              {control}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* The summary line of a stored value, for places that show a value
   without the element (a review page): its own text when kept. */
export function cascadeText(value) {
  return value && typeof value === 'object' && typeof value.text === 'string' ? value.text : '';
}
